import {Action, Actions} from 'platform/components';
import {Box, Heading, HStack, Icon, Text, VStack} from 'platform/foundation';

import {PurchasePriceRuleResponseBody} from '@dms/api/sales';
import {i18n} from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';

import {buildArray, composePath, useNavigate} from 'shared/web';

interface PurchasePriceSystemRuleCardProps {
  rule: PurchasePriceRuleResponseBody;
  canManage: boolean;
}

/**
 * The default rule is always last and cannot be moved, so it shows a lock where the other cards
 * have a drag handle. Mirrors the `DraggableCard` layout to keep the content aligned with them.
 */
export function PurchasePriceSystemRuleCard(props: PurchasePriceSystemRuleCardProps) {
  const navigate = useNavigate();

  const ruleTestId = testIds.settings.purchasePrice(`rule-${props.rule.id}`);

  const handleOpenDetail = () =>
    navigate(composePath(settingsRoutes.purchasePriceRuleDetail, {params: {id: props.rule.id}}));

  const actions = buildArray<Action>()
    .when(props.canManage, {
      type: 'button',
      variant: 'outlined',
      leftIcon: 'image/edit',
      title: i18n.t('general.actions.edit'),
      onClick: handleOpenDetail,
      'data-testid': `${ruleTestId}-edit`,
    })
    .whenNot(props.canManage, {
      type: 'button',
      variant: 'outlined',
      title: i18n.t('general.actions.view'),
      onClick: handleOpenDetail,
      'data-testid': `${ruleTestId}-view`,
    });

  return (
    <Box
      borderRadius="medium"
      backgroundColor="palettes.neutral.10.100"
      border="1px solid"
      borderColor="general.separator"
      padding={4}
      data-testid={ruleTestId}
    >
      <HStack spacing={3} align="center">
        <Icon value="action/lock" color="palettes.neutral.60.100" />

        <HStack justify="space-between" align="center" width="100%" spacing={3}>
          <VStack spacing={2} shrink={1}>
            <Heading size={4} data-testid={`${ruleTestId}-cardHeadingTitle`}>
              {i18n.t('page.settings.purchasePrice.marginRules.systemRule.title')}
            </Heading>
            <Text size="small" color="secondary">
              {i18n.t('page.settings.purchasePrice.marginRules.systemRule.description')}
            </Text>
          </VStack>
          <HStack shrink={0}>
            <Actions actions={actions} size="default" data-testid={`${ruleTestId}-actions`} />
          </HStack>
        </HStack>
      </HStack>
    </Box>
  );
}
